// src/composables/useOnboardingController.ts
import { HOME_PATH } from '@/router/paths';
import { ref, computed } from 'vue'
import { Preferences } from '@capacitor/preferences'
import { useAppContextStore } from '@/stores/appContext'
import { useOnboardingDraftStore } from '@/stores/onboardingDraft'
import { supabase } from '@/lib/supabase'
import { useAnalytics } from '@/composables/useAnalytics'

const STORAGE_KEY = 'harmonia_onboarding'

export const ONBOARDING_STEPS = {
  WELCOME: 0,
  NAME: 1,
  CHILDREN: 2,
  CHILDREN_GENERATED: 3,
  TASKS: 4,
  REWARDS: 5,
  GUIDED_VALIDATION: 6,
  FOUNDERS: 7,
  ACCOUNT: 8,
  SUCCESS: 9,
} as const

export type OnboardingMode = 'create' | 'login-no-household'

type OnboardingPathEvent = 'create_household' | 'login_no_household'

function toOnboardingPath(mode: OnboardingMode): OnboardingPathEvent {
  switch (mode) {
    case 'create': return 'create_household'
    case 'login-no-household': return 'login_no_household'
  }
  const _exhaustive: never = mode
  throw new Error(`Unknown onboarding mode: ${_exhaustive}`)
}

export type ParentType =
  | 'mom'
  | 'dad'
  | 'step-mom'
  | 'step-dad'
  | 'parent'
  | 'grandparent'
  | 'guardian'
  | 'other'

export interface OnboardingState {
  step: number
  mode: OnboardingMode
  anonymousUserId: string | null
  householdId: string | null
  data: {
    parentName: string
    householdName: string
    parentType: ParentType | null
  }
}

function createEmptyState(mode: OnboardingMode = 'create'): OnboardingState {
  return {
    step: ONBOARDING_STEPS.WELCOME,
    mode,
    anonymousUserId: null,
    householdId: null,
    data: {
      parentName: '',
      householdName: '',
      parentType: null,
    },
  }
}

const STEP_NAMES: Record<number, string> = {
  [ONBOARDING_STEPS.WELCOME]: 'welcome',
  [ONBOARDING_STEPS.NAME]: 'name',
  [ONBOARDING_STEPS.CHILDREN]: 'children',
  [ONBOARDING_STEPS.CHILDREN_GENERATED]: 'children_generated',
  [ONBOARDING_STEPS.TASKS]: 'tasks',
  [ONBOARDING_STEPS.REWARDS]: 'rewards',
  [ONBOARDING_STEPS.GUIDED_VALIDATION]: 'guided_validation',
  [ONBOARDING_STEPS.FOUNDERS]: 'founders',
  [ONBOARDING_STEPS.ACCOUNT]: 'account',
  [ONBOARDING_STEPS.SUCCESS]: 'success',
}

// Global singleton state (shared across all component instances)
const state = ref<OnboardingState | null>(null)
const initialized = ref(false)
const onboardingTaskCount = ref(0)
const onboardingRewardCount = ref(0)
let onboardingStartedAt: number | null = null
let stepStartedAt: number | null = null
// Whether the user has interacted with the CURRENT step (focused a field,
// tapped a button…). Reset on every step change; reported on abandon to tell
// "saw the step and left" apart from "engaged then left".
let currentStepInteracted = false
let realtimeChannel: ReturnType<typeof supabase.channel> | null = null

// Resolve the household ID from state or appContext
function resolveHouseholdId(): string | null {
  if (state.value?.householdId) return state.value.householdId
  try {
    return useAppContextStore().currentHouseholdId ?? null
  } catch {
    return null
  }
}

async function refreshCounts(): Promise<void> {
  const householdId = resolveHouseholdId()
  if (!householdId || !state.value) return

  const [tasksResult, rewardsResult] = await Promise.all([
    supabase
      .from('tasks')
      .select('*', { count: 'exact', head: true })
      .eq('household_id', householdId)
      .eq('is_active', true),
    supabase
      .from('rewards')
      .select('*', { count: 'exact', head: true })
      .eq('household_id', householdId),
  ])

  onboardingTaskCount.value = tasksResult.count ?? 0
  onboardingRewardCount.value = rewardsResult.count ?? 0
}

function startRealtimeSync(): void {
  stopRealtimeSync()
  const householdId = resolveHouseholdId()
  if (!householdId) return

  realtimeChannel = supabase
    .channel('onboarding-counts')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `household_id=eq.${householdId}` }, () => {
      refreshCounts()
    })
    .on('postgres_changes', { event: '*', schema: 'public', table: 'rewards', filter: `household_id=eq.${householdId}` }, () => {
      refreshCounts()
    })
    .subscribe()
}

function stopRealtimeSync(): void {
  if (realtimeChannel) {
    supabase.removeChannel(realtimeChannel)
    realtimeChannel = null
  }
}

async function persistState(): Promise<void> {
  if (state.value) {
    await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(state.value) })
  } else {
    await Preferences.remove({ key: STORAGE_KEY })
  }
}

async function loadPersistedState(): Promise<OnboardingState | null> {
  const { value } = await Preferences.get({ key: STORAGE_KEY })
  if (value) {
    try {
      return JSON.parse(value) as OnboardingState
    } catch {
      await Preferences.remove({ key: STORAGE_KEY })
      return null
    }
  }
  return null
}

const STEP_ROUTES: Record<number, string> = {
  [ONBOARDING_STEPS.WELCOME]: '/welcome',
  [ONBOARDING_STEPS.NAME]: '/onboarding/name',
  [ONBOARDING_STEPS.CHILDREN]: '/onboarding/children',
  // Écran fusionné : un état persisté sur l'ancienne étape 3 reprend sur
  // /onboarding/children (sinon la garde du routeur boucle sur la redirection).
  [ONBOARDING_STEPS.CHILDREN_GENERATED]: '/onboarding/children',
  [ONBOARDING_STEPS.TASKS]: '/onboarding/tasks',
  [ONBOARDING_STEPS.REWARDS]: '/onboarding/rewards',
  [ONBOARDING_STEPS.GUIDED_VALIDATION]: '/onboarding/guided-validation',
  [ONBOARDING_STEPS.FOUNDERS]: '/onboarding/founders',
  [ONBOARDING_STEPS.ACCOUNT]: '/onboarding/account',
  [ONBOARDING_STEPS.SUCCESS]: '/onboarding/success',
}

async function getRouter() {
  const mod = await import('@/router/index')
  return mod.default
}

export function resetAll(): void {
  stopRealtimeSync()
  state.value = null
  initialized.value = false
  onboardingTaskCount.value = 0
  onboardingRewardCount.value = 0
  Preferences.remove({ key: STORAGE_KEY })
}

function setTaskCount(count: number) { onboardingTaskCount.value = count }
function setRewardCount(count: number) { onboardingRewardCount.value = count }

export function useOnboardingController() {
  async function init(): Promise<void> {
    if (initialized.value) return
    const persisted = await loadPersistedState()
    if (persisted) {
      state.value = persisted
    }
    initialized.value = true
    // Fetch counts after state is loaded and start realtime sync
    if (state.value) {
      await refreshCounts()
      startRealtimeSync()
    }
  }

  // Emit onboarding_step_viewed for the step the user just landed on. Paired
  // with step_completed, this pins the exact drop-off step in Amplitude.
  function trackStepViewed(step: number): void {
    if (!state.value) return
    const { logOnboardingStepViewed } = useAnalytics()
    void logOnboardingStepViewed(
      step,
      STEP_NAMES[step] ?? `step_${step}`,
      toOnboardingPath(state.value.mode),
    )
  }

  // Mark that the user engaged with the current step (a field focus, a tap…).
  // Idempotent; reset on each step change.
  function markStepInteracted(): void {
    currentStepInteracted = true
  }

  async function startOnboarding(mode: OnboardingMode): Promise<void> {
    state.value = createEmptyState(mode)
    onboardingStartedAt = Date.now()
    stepStartedAt = Date.now()
    currentStepInteracted = false
    await persistState()

    const { logOnboardingStarted } = useAnalytics()
    logOnboardingStarted(toOnboardingPath(mode))
    // The funnel opens on WELCOME — record the first step view too.
    trackStepViewed(state.value.step)
  }

  async function nextStep(): Promise<void> {
    if (!state.value) return

    const completedStep = state.value.step
    const { logOnboardingStepCompleted } = useAnalytics()
    const stepDuration = stepStartedAt ? Math.round((Date.now() - stepStartedAt) / 1000) : undefined
    logOnboardingStepCompleted(
      completedStep,
      STEP_NAMES[completedStep] ?? `step_${completedStep}`,
      stepDuration,
      toOnboardingPath(state.value.mode),
      completedStep === ONBOARDING_STEPS.CHILDREN
        ? useOnboardingDraftStore().draft?.declaredChildrenCount ?? null
        : null,
    )
    stepStartedAt = Date.now()
    currentStepInteracted = false

    // login-no-household mode: skip ACCOUNT (user already authed). ACCOUNT now
    // comes AFTER guided validation, so the skip lands on SUCCESS.
    let next = state.value.step + 1
    // Écran enfants fusionné (design Soleil) : CHILDREN couvre compte ET fiches,
    // CHILDREN_GENERATED n'est plus une étape traversée.
    if (next === ONBOARDING_STEPS.CHILDREN_GENERATED) {
      next = ONBOARDING_STEPS.TASKS
    }
    if (state.value.mode === 'login-no-household' && next === ONBOARDING_STEPS.ACCOUNT) {
      next = ONBOARDING_STEPS.SUCCESS
    }

    if (next > ONBOARDING_STEPS.SUCCESS) {
      await finish(true)
      return
    }

    state.value.step = next
    await persistState()
    trackStepViewed(next)

    const targetRoute = STEP_ROUTES[next]
    if (targetRoute) {
      const router = await getRouter()
      if (router.currentRoute.value.path !== targetRoute) {
        await router.push(targetRoute)
      }
    }
  }

  async function completeStep(stepData: Partial<OnboardingState['data']>): Promise<void> {
    if (!state.value) return
    Object.assign(state.value.data, stepData)
    await nextStep()
  }

  // Saute directement à une étape nommée (utilisé par la garde de complétude :
  // un brouillon incomplet renvoie l'utilisateur à l'étape manquante avant le
  // commit). Positionne l'état et navigue vers la route correspondante.
  const STEP_BY_NAME: Record<'name' | 'children' | 'tasks' | 'rewards', number> = {
    name: ONBOARDING_STEPS.NAME,
    children: ONBOARDING_STEPS.CHILDREN,
    tasks: ONBOARDING_STEPS.TASKS,
    rewards: ONBOARDING_STEPS.REWARDS,
  }
  async function goToStep(stepName: 'name' | 'children' | 'tasks' | 'rewards'): Promise<void> {
    if (!state.value) return
    const target = STEP_BY_NAME[stepName]
    state.value.step = target
    stepStartedAt = Date.now()
    currentStepInteracted = false
    await persistState()
    const targetRoute = STEP_ROUTES[target]
    if (targetRoute) {
      const router = await getRouter()
      if (router.currentRoute.value.path !== targetRoute) {
        await router.push(targetRoute)
      }
    }
  }

  async function prevStep(): Promise<void> {
    if (!state.value) return

    // login-no-household mode never lands on ACCOUNT, so SUCCESS → FOUNDERS
    let prev = state.value.step - 1
    if (state.value.mode === 'login-no-household' && prev === ONBOARDING_STEPS.ACCOUNT) {
      prev = ONBOARDING_STEPS.FOUNDERS
    }
    // Écran enfants fusionné : on ne recule jamais sur CHILDREN_GENERATED.
    if (prev === ONBOARDING_STEPS.CHILDREN_GENERATED) {
      prev = ONBOARDING_STEPS.CHILDREN
    }

    if (prev < ONBOARDING_STEPS.WELCOME) return

    state.value.step = prev
    stepStartedAt = Date.now()
    currentStepInteracted = false
    await persistState()

    const targetRoute = STEP_ROUTES[prev]
    if (targetRoute) {
      const router = await getRouter()
      if (router.currentRoute.value.path !== targetRoute) {
        await router.push(targetRoute)
      }
    }
  }

  // Silent abandon: the app went to the background mid-onboarding. Unlike
  // cancelOnboarding this tears nothing down — the user may come back — it only
  // emits the signal so Amplitude counts background drop-offs, not just explicit
  // cancels. Skipped on the terminal SUCCESS step (already converted).
  function trackAbandonOnPause(): void {
    if (!state.value) return
    if (state.value.step >= ONBOARDING_STEPS.SUCCESS) return
    const abandonedStep = state.value.step
    const timeSpent = onboardingStartedAt
      ? Math.round((Date.now() - onboardingStartedAt) / 1000)
      : undefined
    const stepDurationSeconds = stepStartedAt
      ? Math.round((Date.now() - stepStartedAt) / 1000)
      : undefined
    const { logOnboardingAbandoned } = useAnalytics()
    void logOnboardingAbandoned(
      abandonedStep,
      STEP_NAMES[abandonedStep] ?? `step_${abandonedStep}`,
      timeSpent,
      'app_paused',
      { stepDurationSeconds, interacted: currentStepInteracted },
    )
  }

  async function cancelOnboarding(): Promise<void> {
    // Tears down everything an in-progress onboarding can leave behind:
    //   - the household row created server-side
    //   - the anonymous auth user (via edge function)
    //   - the onboarding state in Capacitor Preferences
    //   - localStorage caches (harmonia_user, available_children, etc.)
    //   - the appContext / userContext Pinia stores
    // Without that last bit, the user comes back to a "fresh" /welcome but
    // the form in OnboardingNamePage is still pre-filled and appContext
    // still references the just-deleted household.
    stopRealtimeSync()

    // Track abandonment before we tear the state down — this is the explicit
    // "user cancelled the onboarding" signal, captured with the step they were
    // on and how long they'd spent. Lets Amplitude build a real abandonment
    // funnel instead of inferring it from started − completed.
    if (state.value) {
      const abandonedStep = state.value.step
      const timeSpent = onboardingStartedAt
        ? Math.round((Date.now() - onboardingStartedAt) / 1000)
        : undefined
      const stepDurationSeconds = stepStartedAt
        ? Math.round((Date.now() - stepStartedAt) / 1000)
        : undefined
      const { logOnboardingAbandoned } = useAnalytics()
      void logOnboardingAbandoned(
        abandonedStep,
        STEP_NAMES[abandonedStep] ?? `step_${abandonedStep}`,
        timeSpent,
        'cancelled',
        { stepDurationSeconds, interacted: currentStepInteracted },
      )
    }

    const householdIdToDelete = state.value?.householdId ?? null
    const anonymousUserIdToDelete = state.value?.anonymousUserId ?? null

    // Reset onboarding-local state first so any UI listening to onboarding
    // stops trying to refresh against soon-to-be-deleted records.
    state.value = null
    onboardingTaskCount.value = 0
    onboardingRewardCount.value = 0
    onboardingStartedAt = null
    stepStartedAt = null
    await persistState()
    // Belt-and-suspenders: drop the persisted Capacitor key explicitly.
    try { await Preferences.remove({ key: STORAGE_KEY }) } catch { /* best-effort cleanup, ignore */ }

    // Delete the household created during onboarding (if any). Non-blocking
    // for navigation: if it fails we still send the user back to /welcome.
    if (householdIdToDelete) {
      try {
        const { householdService } = await import('@/services/household.service')
        await householdService.deleteHousehold(householdIdToDelete)
      } catch (err) {
        console.warn('[Onboarding] failed to delete household on cancel:', err)
      }
    }

    // Delete the anonymous auth user via the edge function. Whether it
    // succeeds or not, we then run a full logout so localStorage and Pinia
    // stores are cleared (otherwise the next /welcome step pre-fills with
    // stale data).
    if (anonymousUserIdToDelete) {
      try {
        const { authAdminService } = await import('@/services/supabase/auth-admin.service')
        await authAdminService.deleteUser(anonymousUserIdToDelete)
      } catch (err) {
        console.warn('[Onboarding] failed to delete anonymous user on cancel:', err)
      }
    }

    // Use the auth store's logout(): it signs out, clears localStorage,
    // resets appContext + userContext. Even if no anonymous user existed,
    // doing this is safe and guarantees a clean slate.
    try {
      const { useAuthStore } = await import('@/stores/auth')
      await useAuthStore().logout()
    } catch (err) {
      console.warn('[Onboarding] logout during cancel failed:', err)
    }

    const router = await getRouter()
    if (router.currentRoute.value.path !== '/welcome') {
      await router.push('/welcome')
    }
  }

  /**
   * Filet de démarrage contre l'onboarding « fantôme ».
   *
   * Le contrôleur n'a qu'une sortie, finish(), appelée par l'écran compte quand
   * la promesse d'authentification lui revient. Quand l'auth sociale repasse par
   * le navigateur externe, elle revient à l'app par un lien profond et cette
   * promesse ne revient JAMAIS à la page : l'état restait gravé à l'étape
   * ACCOUNT dans les Preferences, définitivement. Le foyer, lui, était rattrapé
   * au démarrage suivant par finalizePendingCommit(), si bien que l'utilisateur
   * se retrouvait avec un compte, un foyer, et une app convaincue qu'il n'avait
   * ni l'un ni l'autre : bande de la chouette « créez votre compte » à demeure,
   * et garde du routeur qui le laissait sur /welcome à chaque lancement (il
   * croyait devoir se reconnecter alors que sa session était parfaitement
   * valide). Deux foyers piégés en prod, sans aucun moyen d'en sortir depuis
   * l'app. Cf. le retour client Labrousse du 19/08/2026.
   *
   * On ne purge que le cas sans ambiguïté : session réelle, foyer déjà en base,
   * et aucun brouillon restant à committer. Un brouillon non commité signifie
   * que l'onboarding est vraiment en cours (ou qu'il est incomplet et doit être
   * repris, cf. la garde de complétude de finalizePendingCommit) : on n'y touche
   * pas. Retourne true si un état fantôme a été purgé.
   */
  async function discardGhostOnboarding(): Promise<boolean> {
    await init()
    if (!state.value) return false

    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user || session.user.is_anonymous) return false

    const draftStore = useOnboardingDraftStore()
    if (!draftStore.draft) await draftStore.load()
    if (draftStore.draft && !draftStore.draft.committedAt) return false

    const { supabaseHouseholdService } = await import('@/services/supabase/household.service')
    const households = await supabaseHouseholdService.getUserHouseholds()
    if (!Array.isArray(households) || households.length === 0) return false

    console.warn('[Onboarding] état fantôme détecté (compte et foyer déjà créés) : purge')
    stopRealtimeSync()
    state.value = null
    onboardingTaskCount.value = 0
    onboardingRewardCount.value = 0
    onboardingStartedAt = null
    stepStartedAt = null
    await persistState()
    return true
  }

  /**
   * Sortie unique de l'onboarding : contexte rechargé, puis dashboard.
   *
   * forceReloadContext (pas loadUserHouseholds) : le foyer vient d'être créé par
   * commitDraft, mais le cache TTL 30s peut servir un [] périmé (peuplé plus tôt
   * par le guard router quand le foyer n'existait pas) → la nav suivante verrait
   * 0 foyer et redirigerait vers welcome. forceReloadContext invalide le cache.
   */
  async function landOnDashboard(): Promise<void> {
    const { useAuthStore } = await import('@/stores/auth')
    const authStore = useAuthStore()
    const appContextStore = useAppContextStore()
    if (authStore.user?.id) {
      await appContextStore.forceReloadContext(authStore.user.id)
    }

    const router = await getRouter()
    await router.push(HOME_PATH)
  }

  async function finish(skipStepEvent = false): Promise<void> {
    if (!state.value) {
      // Rien à purger : l'onboarding a déjà été terminé une première fois
      // (commit rejoué au boot par finalizePendingCommit, second passage par
      // l'écran compte, app rechargée après le commit). Ce n'est pas une raison
      // de ne rien faire : les trois appelants de finish() sont des fins de
      // parcours et n'ont aucun autre plan de navigation. Sortir sèchement
      // laissait l'utilisateur sur l'écran de saisie du code, sans erreur et
      // sans issue, alors que son compte ET son foyer existaient déjà.
      onboardingStartedAt = null
      stepStartedAt = null
      stopRealtimeSync()
      await landOnDashboard()
      return
    }

    const pathForEvents: OnboardingPathEvent = toOnboardingPath(state.value.mode)
    if (!skipStepEvent) {
      const { logOnboardingStepCompleted } = useAnalytics()
      const completedStep = state.value.step
      const stepDuration = stepStartedAt ? Math.round((Date.now() - stepStartedAt) / 1000) : undefined
      logOnboardingStepCompleted(
        completedStep,
        STEP_NAMES[completedStep] ?? `step_${completedStep}`,
        stepDuration,
        pathForEvents,
      )
    }

    // Terminal funnel event — fired HERE, the single choke point every
    // completion path goes through (Account finalize in create mode, Success
    // CTA in login-no-household mode, nextStep overflow). Previously it lived
    // on the SUCCESS page's mount, which the create-mode flow never displays —
    // so onboarding_completed silently never reached Amplitude on the main path.
    // Counts come from the draft when it still exists, else from the funnel's
    // in-memory counters (the draft is cleared right after a successful commit).
    try {
      const draftStore = useOnboardingDraftStore()
      const draft = draftStore.draft
      const counts = draft
        ? {
            children: draft.children.length,
            tasks: draft.tasks.length,
            rewards: draft.rewards.length,
            declaredChildrenCount: draft.declaredChildrenCount ?? null,
          }
        : draftStore.lastClearedCounts ?? {
            children: useAppContextStore().availableChildren.length,
            tasks: onboardingTaskCount.value,
            rewards: onboardingRewardCount.value,
            declaredChildrenCount: null,
          }
      const totalDuration = onboardingStartedAt
        ? Math.round((Date.now() - onboardingStartedAt) / 1000)
        : undefined
      const { logOnboardingCompleted } = useAnalytics()
      void logOnboardingCompleted(counts.children, counts.tasks, counts.rewards, totalDuration, pathForEvents, counts.declaredChildrenCount)
    } catch (err) {
      console.warn('[Onboarding] failed to log onboarding_completed:', err)
    }

    onboardingStartedAt = null
    stepStartedAt = null
    stopRealtimeSync()

    // login-no-household: the user is already authenticated but skipped the
    // ACCOUNT step, so finalizeAfterAuth() was never triggered. Call it now
    // to commit the draft under the real uid before clearing state.
    const modeSnapshot = state.value?.mode
    if (modeSnapshot === 'login-no-household') {
      try {
        const { useOnboardingFinalize } = await import('@/composables/useOnboardingFinalize')
        await useOnboardingFinalize().finalizeAfterAuth()
      } catch (err) {
        console.error('[Onboarding] finalizeAfterAuth failed in login-no-household finish:', err)
        // Non-fatal: let the rest of finish() proceed so the user is not stuck.
      }
    }

    const keysToClean = [
      'harmonia_onboarding_anonymous_user_id',
      'harmonia_onboarding_household_id',
      'harmonia_onboarding_children_synced',
      'harmonia_onboarding_tasks_synced',
      'harmonia_onboarding_rewards_synced',
      'harmonia_anonymous',
    ]
    keysToClean.forEach(key => localStorage.removeItem(key))

    state.value = null
    onboardingTaskCount.value = 0
    onboardingRewardCount.value = 0
    await persistState()

    await landOnDashboard()
  }

  function setAnonymousUserId(id: string): void {
    if (state.value) {
      state.value.anonymousUserId = id
      persistState()
    }
  }

  function setHouseholdId(id: string): void {
    if (state.value) {
      state.value.householdId = id
      persistState()
      startRealtimeSync()
    }
  }

  function expectedRoute(): string | null {
    if (!state.value) return null
    return STEP_ROUTES[state.value.step] ?? null
  }

  // NOTE : toutes les étapes avancent EXPLICITEMENT depuis le handler Continue
  // de leur page (nextStep() direct). L'ancien watcher d'auto-avance de
  // CHILDREN a disparu avec l'écran enfants fusionné (design Soleil).
  const canProceed = computed(() => {
    if (!state.value) return false
    switch (state.value.step) {
      case ONBOARDING_STEPS.TASKS:
        return onboardingTaskCount.value >= 4
      case ONBOARDING_STEPS.REWARDS:
        return onboardingRewardCount.value >= 4
      default:
        return false
    }
  })

  return {
    isOnboarding: computed(() => state.value !== null),
    currentStep: computed(() => state.value?.step ?? -1),
    onboardingData: computed(() => state.value?.data ?? createEmptyState().data),
    onboardingMode: computed(() => state.value?.mode ?? 'create'),
    householdId: computed(() => state.value?.householdId ?? null),
    anonymousUserId: computed(() => state.value?.anonymousUserId ?? null),
    canProceed,
    taskCount: computed(() => onboardingTaskCount.value),
    rewardCount: computed(() => onboardingRewardCount.value),

    init,
    startOnboarding,
    nextStep,
    prevStep,
    cancelOnboarding,
    trackAbandonOnPause,
    markStepInteracted,
    completeStep,
    goToStep,
    discardGhostOnboarding,
    finish,
    setAnonymousUserId,
    setHouseholdId,
    setTaskCount,
    setRewardCount,
    refreshCounts,
    expectedRoute,
  }
}
